import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Link,
  Alert,
  CircularProgress,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import { GoogleLogin } from "@react-oauth/google";
import axios from "axios";
import API_BASE_URL from "../../apiConfig";
import { PRIMARY_PINK } from "../../constants";

function SignUpModal({ open, onClose, onSignUp, onSwitchToSignIn }) {
  const [email, setEmail] = useState("");
  const [temporary_password, setTemporaryPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [display_name, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation
    if (!email) {
      setError("Email is required");
      return;
    }
    
    if (!temporary_password) {
      setError("Password is required");
      return;
    }
    
    if (temporary_password !== confirmPassword) {
      setError("Passwords don't match!");
      return;
    }
    
    if (temporary_password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/users`, {
        email,
        display_name: display_name || null,
        temporary_password,
        google_id: null
      }, {
        withCredentials: true
      });
      
      await onSignUp(response.data);
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async (credentialResponse) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          idToken: credentialResponse.credential,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          throw new Error("Google authentication failed. Please try again.");
        } else if (response.status === 404) {
          throw new Error("Account not found. Please sign up first.");
        } else {
          throw new Error(errorData.error || "Google authentication failed");
        }
      }

      const userData = await response.json();
      await onSignUp({ googleUser: userData });
    } catch (error) {
      console.error("Google sign up error:", error);
      setError(error.message || "Google authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    setEmail("");
    setTemporaryPassword("");
    setConfirmPassword("");
    setDisplayName("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography variant="h5" component="div">
          Sign Up
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin
              onSuccess={handleGoogleSignUp}
              onError={() => setError("Google sign-up failed. Please try again.")}
              disabled={loading}
              useOneTap
            />
          </Box>
          
          <Divider sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <TextField
            margin="normal"
            fullWidth
            id="display_name"
            label="Display Name (Optional)"
            name="display_name"
            autoComplete="name"
            autoFocus
            value={display_name}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="temporary_password"
            label="Password"
            type="password"
            id="temporary_password"
            autoComplete="new-password"
            value={temporary_password}
            onChange={(e) => setTemporaryPassword(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{" "}
              <Link
                component="button"
                variant="body2"
                onClick={onSwitchToSignIn}
                sx={{ 
                  cursor: "pointer",
                  color: PRIMARY_PINK,
                  fontWeight: 500,
                  "&:hover": {
                    color: "#1565c0"
                  }
                }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          fullWidth 
          variant="outlined"
          disabled={loading}
          sx={{
            color: "#000000",
            borderColor: "#000000",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            "&:hover": {
              borderColor: "#000000",
              backgroundColor: "rgba(0,0,0,0.08)"
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          fullWidth 
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: "#000000",
            color: "#FFFFFF",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            "&:hover": {
              bgcolor: "#333333"
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Sign Up"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SignUpModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSignUp: PropTypes.func.isRequired,
  onSwitchToSignIn: PropTypes.func.isRequired,
};

export default SignUpModal; 